import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Divider,
  Stack,
  Tooltip,
  Button,
  Menu,
  MenuItem,
  Drawer,
  Avatar,
  useMediaQuery,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Close,
  Brightness4,
  Brightness7,
  NotificationsNoneOutlined as NotificationsIcon,
  KeyboardArrowDown,
  PersonOutline,
  AssignmentOutlined,
  ScienceOutlined,
  FactCheckOutlined,
  SettingsOutlined,
  SwapHoriz,
  Logout as LogoutIcon,
  Person,
  LockResetOutlined,
  BiotechOutlined,
  ChevronLeft,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useAuth } from "../../contexts/AuthContext";
import { useThemeMode } from "../../contexts/ThemeModeContext";
import { useNavigate, useLocation } from "react-router-dom";
import { IconLogo } from "../logo/FullLogo";

// Sub-views
import LabDashboardHome from "./LabDashboardHome";
import CreateLabOrder from "./CreateLabOrder";
import SamplesQueue from "./SamplesQueue";
import ResultsList from "./ResultsList";
import LabReportView from "./LabReportView";
import EnterResult from "./EnterResult";
import CollectSampleModal from "./CollectSampleModal";
import AnalyzerManagement from "./AnalyzerManagement";
import PatientsView from "../Layout/PatientsView";
import PatientRegistration from "../Clinician/PatientRegistration";
import UserSettings from "../UserSettings";
import ProfileSettings from "../Admin/ProfileSettings";
import ChangePasswordDialog from "../Auth/ChangePassword";
import NotificationListener from "../Common/NotificationListener";
import ReferralDetailsDialog from "./ReferralDetailsDialog";
import {
  getUnreadNotifications,
  markAllNotificationsAsRead,
} from "../../services/notifications";

const DRAWER_WIDTH = 240;
const COLLAPSED_DRAWER_WIDTH = 68;

// Custom clean SVG Dashboard Icon matching the screenshot
const DashboardIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

export default function LaboratoryDashboard() {
  const { user, logout } = useAuth();
  const { mode, toggleTheme } = useThemeMode();
  const isDark = mode === "dark";
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Theme Colors
  const textColorPrimary = isDark ? "#F8FAFC" : "#0F172A";
  const textColorSecondary = isDark ? "#94A3B8" : "#64748B";
  const cardBg = isDark ? "#1E1E1E" : "#FFFFFF";
  const sidebarBg = isDark ? "#141414" : "#FFFFFF";
  const mainBg = isDark ? "#0A0A0A" : "#F8FAFC";
  const borderColor = isDark ? "rgba(255,255,255,0.08)" : "#E2E8F0";
  const primaryBlue = "#2563EB";
  const activeBg = isDark ? "rgba(37, 99, 235, 0.15)" : "#EEF4FF";

  // State - default collapsed / closed
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentView, setCurrentView] = useState("dashboard");

  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [unreadCount, setUnreadCount] = useState(3);

  // Profile menu
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);

  // Modals
  const [patientRegistrationOpen, setPatientRegistrationOpen] = useState(false);
  const [collectSampleOpen, setCollectSampleOpen] = useState(false);
  const [collectSampleOrderId, setCollectSampleOrderId] = useState(null);

  // Selected Patient for order creation
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedReferralData, setSelectedReferralData] = useState(null);

  // Incoming Referral Dialog
  const [selectedReferralNotif, setSelectedReferralNotif] = useState(null);
  const [referralDialogOpen, setReferralDialogOpen] = useState(false);

  // View Report & Enter Result IDs
  const [viewReportResultId, setViewReportResultId] = useState(null);
  const [enterResultOrderItemId, setEnterResultOrderItemId] = useState(null);
  const [enterResultSpecimenId, setEnterResultSpecimenId] = useState(null);
  const [enterResultOrderId, setEnterResultOrderId] = useState(null);

  // Refresh key — incrementing forces child components to re-mount and re-fetch data
  const [refreshKey, setRefreshKey] = useState(0);

  // Formatted user name
  const userTitle =
    user?.role === "radiologist" || user?.role === "clinician" || !user?.role
      ? "Dr. "
      : "";
  const userFullName = user?.firstName
    ? `${userTitle}${user.firstName} ${user.lastName || ""}`.trim()
    : user?.name || user?.email || "Laboratory Staff";
  const userDepartment = user?.department || "Laboratory Department";

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const res = await getUnreadNotifications();
      const list = res?.notifications || res?.data?.data || res?.data || (Array.isArray(res) ? res : []);
      setNotifications(Array.isArray(list) ? list : []);
      setUnreadCount(Array.isArray(list) ? list.length : res?.count || 0);
    } catch (err) {
      /* silent */
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsAsRead();
      setNotifications([]);
      setUnreadCount(0);
    } catch (err) {
      /* silent */
    }
  };

  const handleNotificationClick = (notif) => {
    setNotifAnchorEl(null);
    if (notif.type === "lab_test_request") {
      setSelectedReferralNotif(notif);
      setReferralDialogOpen(true);
    } else if (notif.type === "lab_report_ready") {
      if (notif.labReportId) {
        setViewReportResultId(notif.labReportId);
        setCurrentView("reportView");
      }
    }
  };

  const handleAcceptReferralSuccess = (data, notif) => {
    setReferralDialogOpen(false);
    setNotifications((prev) => prev.filter((n) => n._id !== notif._id));
    setUnreadCount((prev) => Math.max(0, prev - 1));

    const pat = data?.patient || notif.patientSnapshot || {
      _id: notif.patientId,
      patientName: notif.patientName,
      mrn: notif.patientSnapshot?.mrn,
      firstName: notif.patientSnapshot?.firstName,
      lastName: notif.patientSnapshot?.lastName,
      sex: notif.patientSnapshot?.sex,
      birthDate: notif.patientSnapshot?.birthDate,
    };
    setSelectedPatient(pat);
    setSelectedReferralData({
      patient: pat,
      requestedTests: notif.referralDetails?.requestedTests || [],
      requestingDoctor: notif.senderName || "",
      priority: notif.referralDetails?.urgency || notif.priority || "routine",
      clinicalNotes: notif.referralDetails?.clinicalNotes || "",
      studyUID: notif.studyUID || notif.referralDetails?.studyUID,
      referralId: data?.referralId || notif.referralDetails?.referralId || notif._id,
    });
    setCurrentView("createOrder");
  };

  // Navigation Items matching the design screenshot + analyzer connectivity
  const menuItems = [
    { key: "dashboard", text: "Dashboard", icon: <DashboardIcon /> },
    { key: "patients", text: "Patients", icon: <PersonOutline sx={{ fontSize: 20 }} /> },
    {
      key: "createOrder",
      text: "Create Orders",
      icon: <AssignmentOutlined sx={{ fontSize: 20 }} />,
    },
    { key: "samples", text: "Samples", icon: <ScienceOutlined sx={{ fontSize: 20 }} /> },
    { key: "results", text: "Results", icon: <FactCheckOutlined sx={{ fontSize: 20 }} /> },
    { key: "analyzers", text: "Analyzers", icon: <BiotechOutlined sx={{ fontSize: 20 }} /> },
    { key: "settings", text: "Settings", icon: <SettingsOutlined sx={{ fontSize: 20 }} /> },
  ];

  // Actions
  const handleCollectSample = (orderId) => {
    setCollectSampleOrderId(orderId);
    setCollectSampleOpen(true);
  };

  const handleViewReport = (resultId) => {
    setViewReportResultId(resultId);
    setCurrentView("reportView");
  };

  const handleEnterResult = (orderItemId, specimenId, orderId) => {
    setEnterResultOrderItemId(orderItemId);
    setEnterResultSpecimenId(specimenId);
    setEnterResultOrderId(orderId);
    setCurrentView("enterResult");
  };

  const handleSwitchToPACS = () => {
    navigate("/radiologist");
  };

  const handleLogout = () => {
    setProfileAnchorEl(null);
    logout();
    navigate("/login");
  };

  const isCollapsed = !isMobile && !drawerOpen;

  // Sidebar Component
  const sidebarContent = (
    <Box
      sx={{
        width: isCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: sidebarBg,
        borderRight: `1px solid ${borderColor}`,
        py: 2,
        transition: "width 0.2s ease-in-out",
        overflowX: "hidden",
      }}
    >
      {/* Top Logo & DycoLIS Branding Header */}
      {isCollapsed ? (
        <Box
          onClick={() => setDrawerOpen(true)}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            pb: 1.8,
            mb: 1.5,
            borderBottom: `1px solid ${borderColor}`,
            cursor: "pointer",
          }}
        >
          <Tooltip title="Expand DycoLIS Menu" placement="right" arrow>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconLogo size={28} />
            </Box>
          </Tooltip>
        </Box>
      ) : (
        <Box
          sx={{
            px: 2,
            pb: 1.8,
            mb: 1.5,
            borderBottom: `1px solid ${borderColor}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box
            onClick={() => setCurrentView("dashboard")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              cursor: "pointer",
              minWidth: 0,
            }}
          >
            <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
              <IconLogo size={28} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: "'Satoshi', sans-serif",
                  fontWeight: 800,
                  fontSize: "1.15rem",
                  color: textColorPrimary,
                  lineHeight: 1.1,
                  letterSpacing: "-0.01em",
                }}
              >
                Dyco<Box component="span" sx={{ color: "#2563EB" }}>LIS</Box>
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Satoshi', sans-serif",
                  fontSize: "0.68rem",
                  color: textColorSecondary,
                  fontWeight: 500,
                  mt: 0.2,
                }}
              >
                Laboratory System
              </Typography>
            </Box>
          </Box>
          {!isMobile && (
            <IconButton
              size="small"
              onClick={() => setDrawerOpen(false)}
              sx={{ color: textColorSecondary, p: 0.5 }}
            >
              <ChevronLeft sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </Box>
      )}

      {/* Menu List */}
      <List sx={{ px: isCollapsed ? 1 : 1.5, flex: 1 }}>
        {menuItems.map((item) => {
          const isActive = currentView === item.key;
          const button = (
            <ListItemButton
              key={item.key}
              onClick={() => {
                setCurrentView(item.key);
                if (isMobile) setDrawerOpen(false);
              }}
              sx={{
                borderRadius: "10px",
                mb: 0.6,
                px: isCollapsed ? 1 : 1.8,
                py: 0.9,
                justifyContent: isCollapsed ? "center" : "flex-start",
                bgcolor: isActive ? activeBg : "transparent",
                color: isActive ? primaryBlue : textColorSecondary,
                "&:hover": {
                  bgcolor: isActive
                    ? activeBg
                    : isDark
                    ? "rgba(255,255,255,0.04)"
                    : "rgba(0,0,0,0.03)",
                  color: isActive ? primaryBlue : textColorPrimary,
                },
                transition: "all 0.15s ease",
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: isCollapsed ? "unset" : 32,
                  color: isActive ? primaryBlue : textColorSecondary,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!isCollapsed && (
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontFamily: "'Satoshi', sans-serif",
                    fontWeight: isActive ? 700 : 500,
                    fontSize: "0.85rem",
                    whiteSpace: "nowrap",
                  }}
                />
              )}
            </ListItemButton>
          );

          return isCollapsed ? (
            <Tooltip key={item.key} title={item.text} placement="right" arrow>
              {button}
            </Tooltip>
          ) : (
            button
          );
        })}
      </List>

      {/* Bottom Menu Items */}
      <Box sx={{ px: isCollapsed ? 1 : 1.5, mt: "auto" }}>
        <Divider sx={{ borderColor: borderColor, mb: 1.2 }} />

        {/* Switch to PACS */}
        {isCollapsed ? (
          <Tooltip title="Switch to PACS" placement="right" arrow>
            <ListItemButton
              onClick={handleSwitchToPACS}
              sx={{
                borderRadius: "10px",
                mb: 0.6,
                px: 1,
                py: 0.9,
                justifyContent: "center",
                color: textColorSecondary,
                "&:hover": {
                  bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
                  color: textColorPrimary,
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: "unset", color: textColorSecondary, justifyContent: "center" }}>
                <SwapHoriz sx={{ fontSize: 20 }} />
              </ListItemIcon>
            </ListItemButton>
          </Tooltip>
        ) : (
          <ListItemButton
            onClick={handleSwitchToPACS}
            sx={{
              borderRadius: "10px",
              mb: 0.6,
              px: 1.8,
              py: 0.9,
              color: textColorSecondary,
              "&:hover": {
                bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
                color: textColorPrimary,
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 32, color: textColorSecondary }}>
              <SwapHoriz sx={{ fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText
              primary="Switch to PACS"
              primaryTypographyProps={{
                fontFamily: "'Satoshi', sans-serif",
                fontWeight: 500,
                fontSize: "0.85rem",
              }}
            />
          </ListItemButton>
        )}

        {/* Logout */}
        {isCollapsed ? (
          <Tooltip title="Logout" placement="right" arrow>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                borderRadius: "10px",
                px: 1,
                py: 0.9,
                justifyContent: "center",
                color: "#EF4444",
                "&:hover": {
                  bgcolor: isDark ? "rgba(239, 68, 68, 0.1)" : "#FEF2F2",
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: "unset", color: "#EF4444", justifyContent: "center" }}>
                <LogoutIcon sx={{ fontSize: 19 }} />
              </ListItemIcon>
            </ListItemButton>
          </Tooltip>
        ) : (
          <ListItemButton
            onClick={handleLogout}
            sx={{
              borderRadius: "10px",
              px: 1.8,
              py: 0.9,
              color: "#EF4444",
              "&:hover": {
                bgcolor: isDark ? "rgba(239, 68, 68, 0.1)" : "#FEF2F2",
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 32, color: "#EF4444" }}>
              <LogoutIcon sx={{ fontSize: 19 }} />
            </ListItemIcon>
            <ListItemText
              primary="Logout"
              primaryTypographyProps={{
                fontFamily: "'Satoshi', sans-serif",
                fontWeight: 600,
                fontSize: "0.85rem",
              }}
            />
          </ListItemButton>
        )}
      </Box>
    </Box>
  );

  // Render View Content
  const renderContent = () => {
    switch (currentView) {
      case "dashboard":
        return (
          <LabDashboardHome
            isDark={isDark}
            onRegisterPatient={() => setPatientRegistrationOpen(true)}
            onCreateOrder={() => setCurrentView("createOrder")}
            onCollectSample={handleCollectSample}
            onViewReport={handleViewReport}
            onEnterResult={handleEnterResult}
            onNavigateToSamples={() => setCurrentView("samples")}
            onNavigateToResults={() => setCurrentView("results")}
          />
        );
      case "patients":
        return (
          <PatientsView
            key={`lis-patients-${refreshKey}`}
            isDark={isDark}
            systemSource="LIS"
            onSelectPatient={(p) => {
              setSelectedPatient(p);
              setCurrentView("createOrder");
            }}
            onCreateOrder={(p) => {
              setSelectedPatient(p);
              setCurrentView("createOrder");
            }}
          />
        );
      case "createOrder":
        return (
          <CreateLabOrder
            isDark={isDark}
            preselectedPatient={selectedPatient}
            preselectedReferral={selectedReferralData}
            onOrderCreated={(orderData, destination) => {
              setSelectedPatient(null);
              setSelectedReferralData(null);
              setRefreshKey((k) => k + 1);
              if (destination === "dashboard") {
                setCurrentView("dashboard");
              } else {
                setCurrentView("samples");
              }
            }}
            onCancel={() => {
              setSelectedPatient(null);
              setSelectedReferralData(null);
              setCurrentView("dashboard");
            }}
          />
        );
      case "samples":
        return (
          <SamplesQueue
            key={`samples-${refreshKey}`}
            isDark={isDark}
            onCollectSample={handleCollectSample}
            onEnterResult={handleEnterResult}
            onViewReport={handleViewReport}
          />
        );
      case "results":
        return (
          <ResultsList
            isDark={isDark}
            onEnterResult={handleEnterResult}
            onViewReport={handleViewReport}
          />
        );
      case "reportView":
        return (
          <LabReportView
            isDark={isDark}
            resultId={viewReportResultId}
            onBack={() => setCurrentView("results")}
            onEdit={(reportId) => {
              setCurrentView("enterResult");
            }}
          />
        );
      case "enterResult":
        return (
          <EnterResult
            isDark={isDark}
            orderItemId={enterResultOrderItemId}
            specimenId={enterResultSpecimenId}
            orderId={enterResultOrderId}
            onSaved={() => setCurrentView("results")}
            onCancel={() => setCurrentView("results")}
          />
        );
      case "analyzers":
        return <AnalyzerManagement isDark={isDark} />;
      case "profile":
        return <ProfileSettings isDark={isDark} />;
      case "settings":
        return <UserSettings isDark={isDark} />;
      default:
        return (
          <LabDashboardHome
            isDark={isDark}
            onRegisterPatient={() => setPatientRegistrationOpen(true)}
            onCreateOrder={() => setCurrentView("createOrder")}
            onCollectSample={handleCollectSample}
            onViewReport={handleViewReport}
            onEnterResult={handleEnterResult}
            onNavigateToSamples={() => setCurrentView("samples")}
            onNavigateToResults={() => setCurrentView("results")}
          />
        );
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: mainBg }}>
      <NotificationListener />

      {/* Sidebar Navigation */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              boxSizing: "border-box",
              border: "none",
              bgcolor: sidebarBg,
              backgroundImage: "none",
            },
          }}
        >
          {sidebarContent}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: isCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH,
            flexShrink: 0,
            whiteSpace: "nowrap",
            boxSizing: "border-box",
            "& .MuiDrawer-paper": {
              width: isCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH,
              boxSizing: "border-box",
              border: "none",
              borderRight: `1px solid ${borderColor}`,
              bgcolor: sidebarBg,
              backgroundImage: "none",
              transition: "width 0.2s ease-in-out",
              overflowX: "hidden",
            },
          }}
        >
          {sidebarContent}
        </Drawer>
      )}

      {/* Main Content View */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          overflowX: "hidden",
        }}
      >
        {/* Header Bar matching Screenshot */}
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: 0.6,
            minHeight: 46,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: mainBg,
            borderBottom: `1px solid ${borderColor}`,
          }}
        >
          {/* Left Title & Subtitle */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              onClick={() => setDrawerOpen((prev) => !prev)}
              sx={{ color: textColorPrimary, p: 0.5 }}
              title={drawerOpen ? "Collapse sidebar" : "Expand sidebar"}
            >
              <MenuIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <Box>
              <Typography
                sx={{
                  fontFamily: "'Satoshi', sans-serif",
                  fontSize: { xs: "1.05rem", md: "1.15rem" },
                  fontWeight: 800,
                  color: textColorPrimary,
                  lineHeight: 1.1,
                  letterSpacing: "-0.02em",
                }}
              >
                DycoLIS
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Satoshi', sans-serif",
                  fontSize: "0.68rem",
                  color: textColorSecondary,
                  fontWeight: 400,
                  lineHeight: 1,
                }}
              >
                Welcome back, {userFullName}
              </Typography>
            </Box>
          </Box>

          {/* Right Notifications & User Profile */}
          <Stack direction="row" spacing={2} alignItems="center">
            {/* Theme toggle */}
            <Tooltip title={isDark ? "Light mode" : "Dark mode"}>
              <IconButton onClick={toggleTheme} sx={{ color: textColorSecondary, p: 1 }}>
                {isDark ? <Brightness7 sx={{ fontSize: 20 }} /> : <Brightness4 sx={{ fontSize: 20 }} />}
              </IconButton>
            </Tooltip>

            {/* Notification Bell with Red Badge */}
            <Tooltip title="Notifications">
              <IconButton
                onClick={(e) => setNotifAnchorEl(e.currentTarget)}
                sx={{
                  bgcolor: isDark ? "#1E1E1E" : "#FFFFFF",
                  border: `1px solid ${borderColor}`,
                  p: 1.1,
                  borderRadius: "50%",
                  color: textColorPrimary,
                }}
              >
                <Badge
                  badgeContent={unreadCount}
                  sx={{
                    "& .MuiBadge-badge": {
                      bgcolor: "#EF4444",
                      color: "#FFFFFF",
                      fontSize: "0.65rem",
                      height: 16,
                      minWidth: 16,
                      px: 0.5,
                      fontWeight: 700,
                    },
                  }}
                >
                  <NotificationsIcon sx={{ fontSize: 20 }} />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* User Profile Pill Widget */}
            <Box
              onClick={(e) => setProfileAnchorEl(e.currentTarget)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 0.5,
                pr: 1.2,
                borderRadius: "30px",
                cursor: "pointer",
                transition: "all 0.15s ease",
                "&:hover": {
                  bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
                },
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: isDark ? "#2D3748" : "#F1F5F9",
                  color: textColorPrimary,
                  border: `1px solid ${borderColor}`,
                }}
              >
                <Person sx={{ fontSize: 18, color: textColorSecondary }} />
              </Avatar>

              <Box sx={{ display: { xs: "none", sm: "block" }, textAlign: "left" }}>
                <Typography
                  sx={{
                    fontFamily: "'Satoshi', sans-serif",
                    fontWeight: 700,
                    fontSize: "0.78rem",
                    color: textColorPrimary,
                    lineHeight: 1.15,
                  }}
                >
                  {userFullName}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'Satoshi', sans-serif",
                    fontSize: "0.66rem",
                    color: textColorSecondary,
                    fontWeight: 400,
                  }}
                >
                  {userDepartment}
                </Typography>
              </Box>

              <KeyboardArrowDown sx={{ color: textColorSecondary, fontSize: 18 }} />
            </Box>
          </Stack>
        </Box>

        {/* Notifications Menu */}
        <Menu
          anchorEl={notifAnchorEl}
          open={Boolean(notifAnchorEl)}
          onClose={() => setNotifAnchorEl(null)}
          PaperProps={{
            sx: {
              width: 320,
              maxHeight: 400,
              bgcolor: cardBg,
              border: `1px solid ${borderColor}`,
              borderRadius: "12px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
            },
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, color: textColorPrimary }}
            >
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Button
                size="small"
                onClick={handleMarkAllRead}
                sx={{ fontSize: "0.7rem", color: primaryBlue, textTransform: "none" }}
              >
                Mark all read
              </Button>
            )}
          </Box>
          <Divider sx={{ borderColor }} />
          {notifications.length === 0 ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography sx={{ fontSize: "0.82rem", color: textColorSecondary }}>
                No new notifications
              </Typography>
            </Box>
          ) : (
            notifications.map((notif, i) => (
              <MenuItem
                key={notif._id || i}
                onClick={() => handleNotificationClick(notif)}
                sx={{
                  py: 1.5,
                  px: 2,
                  whiteSpace: "normal",
                  borderBottom: `1px solid ${borderColor}`,
                  "&:hover": { bgcolor: isDark ? "rgba(255,255,255,0.05)" : "#F8FAFC" },
                }}
              >
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", width: "100%" }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "8px",
                      bgcolor: "rgba(124, 58, 237, 0.1)",
                      color: "#7C3AED",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      mt: 0.25,
                      fontSize: "0.9rem",
                    }}
                  >
                    📋
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{ fontSize: "0.82rem", fontWeight: 700, color: textColorPrimary, lineHeight: 1.3 }}
                    >
                      {notif.message || notif.title || "Lab Test Request"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.72rem", color: textColorSecondary, mt: 0.4 }}>
                      {notif.patientName ? `${notif.patientName} • ` : ""}
                      {notif.timestamp ? new Date(notif.timestamp).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : ""}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            ))
          )}
        </Menu>

        {/* Profile Dropdown Menu */}
        <Menu
          anchorEl={profileAnchorEl}
          open={Boolean(profileAnchorEl)}
          onClose={() => setProfileAnchorEl(null)}
          PaperProps={{
            sx: {
              width: 200,
              bgcolor: cardBg,
              border: `1px solid ${borderColor}`,
              borderRadius: "12px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
              p: 0.5,
            },
          }}
        >
          <MenuItem
            onClick={() => {
              setProfileAnchorEl(null);
              setCurrentView("profile");
            }}
            sx={{ borderRadius: "8px", py: 1 }}
          >
            <ListItemIcon>
              <Person sx={{ color: textColorSecondary, fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 500 }}>
              Profile
            </ListItemText>
          </MenuItem>

          <MenuItem
            onClick={() => {
              setProfileAnchorEl(null);
              setCurrentView("settings");
            }}
            sx={{ borderRadius: "8px", py: 1 }}
          >
            <ListItemIcon>
              <SettingsOutlined sx={{ color: textColorSecondary, fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 500 }}>
              Settings
            </ListItemText>
          </MenuItem>

          <MenuItem
            onClick={() => {
              setProfileAnchorEl(null);
              setShowPasswordDialog(true);
            }}
            sx={{ borderRadius: "8px", py: 1 }}
          >
            <ListItemIcon>
              <LockResetOutlined sx={{ color: textColorSecondary, fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 500 }}>
              Change Password
            </ListItemText>
          </MenuItem>

          <Divider sx={{ borderColor: borderColor, my: 0.5 }} />

          <MenuItem onClick={handleLogout} sx={{ borderRadius: "8px", py: 1 }}>
            <ListItemIcon>
              <LogoutIcon sx={{ color: "#EF4444", fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText
              primaryTypographyProps={{
                fontSize: "0.85rem",
                color: "#EF4444",
                fontWeight: 600,
              }}
            >
              Logout
            </ListItemText>
          </MenuItem>
        </Menu>

        {/* Page Content Body */}
        <Box sx={{ flex: 1, p: { xs: 0.75, sm: 1 }, minWidth: 0 }}>{renderContent()}</Box>
      </Box>

      {/* Patient Registration Modal */}
      {patientRegistrationOpen && (
        <PatientRegistration
          open={patientRegistrationOpen}
          onClose={() => setPatientRegistrationOpen(false)}
          systemSource="LIS"
          onSuccess={() => {
            setPatientRegistrationOpen(false);
            // Force the Patients list to re-mount so the new patient appears
            // immediately (no manual refresh / filter change needed).
            setRefreshKey((k) => k + 1);
          }}
          onCreateOrder={(patient) => {
            setPatientRegistrationOpen(false);
            setSelectedPatient(patient);
            setCurrentView("createOrder");
          }}
        />
      )}

      {/* Collect Sample Modal */}
      {collectSampleOpen && (
        <CollectSampleModal
          open={collectSampleOpen}
          orderId={collectSampleOrderId}
          isDark={isDark}
          onClose={() => {
            setCollectSampleOpen(false);
            setCollectSampleOrderId(null);
          }}
          onCollected={() => {
            setCollectSampleOpen(false);
            setCollectSampleOrderId(null);
            setRefreshKey(k => k + 1);
          }}
        />
      )}

      {/* Change Password Dialog */}
      {showPasswordDialog && (
        <ChangePasswordDialog
          open={showPasswordDialog}
          onClose={() => setShowPasswordDialog(false)}
        />
      )}

      {/* Incoming Referral Details Dialog */}
      {referralDialogOpen && (
        <ReferralDetailsDialog
          open={referralDialogOpen}
          onClose={() => setReferralDialogOpen(false)}
          notification={selectedReferralNotif}
          onAcceptSuccess={handleAcceptReferralSuccess}
          isDark={isDark}
        />
      )}
    </Box>
  );
}
